import type {
  Me,
  Loan, LoanListItem, CreateLoanRequest, UpdateLoanRequest,
  LoanNote, CreateNoteRequest,
  LoanRequest, CreateRequestRequest, UpdateRequestRequest,
  LoanImportRequest, LoanImportResult, LoanImportRow,
  LoanImportSummary,
  PortfolioExport,
  SemiAnnualReport, CreateSemiAnnualReportRequest,
  Invoice1544, CreateInvoice1544Request,
  AuditEntry,
  VersionInfo,
  ImpersonationConfig,
} from '@app/shared';
import { IMPERSONATION_HEADER } from '@app/shared';
import { InteractionRequiredAuthError } from '@azure/msal-browser';
import { runChunkedImport, type ImportProgress } from './import/chunkedImport';
import { apiTokenRequest, loginRequest, msalInstance } from './auth/msalConfig';

/**
 * Last token-acquisition failure, for the sign-in screen to explain itself.
 *
 * Without this the app is silent about the most likely reason a sign-in appears
 * to fail: the token was never obtained, the request went out unauthenticated,
 * the API answered 401, and `me` stayed null — which renders as an instant bounce
 * back to the sign-in prompt with nothing said.
 */
let lastTokenError: string | null = null;
export const getLastTokenError = () => lastTokenError;

async function getAccessToken(): Promise<string | null> {
  const account = msalInstance.getActiveAccount() ?? msalInstance.getAllAccounts()[0];
  if (!account) return null;
  if (!apiTokenRequest.scopes.length) {
    lastTokenError = 'VITE_API_SCOPE is not configured.';
    return null;
  }
  try {
    const result = await msalInstance.acquireTokenSilent({ ...apiTokenRequest, account });
    lastTokenError = null;
    return result.accessToken;
  } catch (e) {
    // Consent missing or revoked, MFA required, or the cached account no longer
    // has a usable session: silence here strands the user on the sign-in page
    // forever, because a silent retry will keep failing the same way. Send them
    // back through the interactive flow instead.
    if (e instanceof InteractionRequiredAuthError) {
      await msalInstance.acquireTokenRedirect({ ...apiTokenRequest, account });
      return null;
    }
    lastTokenError = e instanceof Error ? e.message : String(e);
    console.error('[auth] could not acquire an API token', e);
    return null;
  }
}

/** Restart sign-in from scratch, letting the user pick a different account. */
export async function reauthenticate(): Promise<void> {
  lastTokenError = null;
  await msalInstance.loginRedirect(loginRequest);
}

/**
 * Error thrown for any non-2xx API response. Carries the HTTP status and, when
 * the server supplied one on an unexpected 500, the `errorId` (the Functions
 * invocation id) — surface this to the user so a bug report can be traced to the
 * exact server-side log entry in Application Insights.
 */
export class ApiError extends Error {
  constructor(
    public status: number,
    message: string,
    public errorId?: string,
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

/**
 * Impersonation testing aid (non-production). Kept in localStorage so the
 * borrowed identity survives a refresh — a tester driving a workflow shouldn't
 * be dropped back to their own account by an F5.
 *
 * Setting this has no effect unless the API also has IMPERSONATION_ENABLED=true;
 * the server ignores the header otherwise. See shared/src/index.ts.
 */
const IMPERSONATION_STORAGE_KEY = 'umwsb.impersonate';

export function getImpersonation(): string | null {
  try {
    return localStorage.getItem(IMPERSONATION_STORAGE_KEY);
  } catch {
    return null;
  }
}

export function setImpersonation(key: string | null): void {
  try {
    if (key) localStorage.setItem(IMPERSONATION_STORAGE_KEY, key);
    else localStorage.removeItem(IMPERSONATION_STORAGE_KEY);
  } catch {
    /* storage unavailable (private mode) — impersonation just won't persist */
  }
}

async function http<T>(path: string, init?: RequestInit): Promise<T> {
  const headers = new Headers(init?.headers);
  const token = await getAccessToken();
  if (token) headers.set('Authorization', `Bearer ${token}`);
  const impersonate = getImpersonation();
  if (impersonate) headers.set(IMPERSONATION_HEADER, impersonate);
  if (init?.body && !headers.has('Content-Type')) headers.set('Content-Type', 'application/json');
  const r = await fetch(path, { ...init, headers });
  // Any write can move a field the loans grid shows, so the cached list is
  // dropped on every non-GET. Deliberately blunt: `lineStatus` is derived from
  // the loan's open package, so creating a package changes a list column without
  // touching dbo.loans, and a per-endpoint invalidation map would have to know
  // that. Dropping one array costs nothing.
  if (init?.method && init.method !== 'GET') invalidateLoanList();
  if (!r.ok) {
    // Prefer the structured `{ error, errorId }` body the API returns; fall back
    // to raw text (or the status line) if the body isn't the expected JSON shape.
    let message = `${r.status} ${r.statusText}`;
    let errorId: string | undefined;
    const body = await r.json().catch(() => null);
    if (body && typeof body === 'object' && typeof (body as { error?: unknown }).error === 'string') {
      message = (body as { error: string }).error;
      const id = (body as { errorId?: unknown }).errorId;
      if (typeof id === 'string') errorId = id;
    }
    throw new ApiError(r.status, message, errorId);
  }
  if (r.status === 204) return undefined as T;
  return r.json() as Promise<T>;
}

// Auth
export const getMe = () => http<Me>('/api/me');
export const getHealth = () => http<{ ok: boolean; db?: boolean; error?: string }>('/api/health');
export const getVersion = () => http<VersionInfo>('/api/version');
export const getImpersonationConfig = () => http<ImpersonationConfig>('/api/impersonation');

// Loans
// The grid fetches the whole permitted set and narrows it in the browser, so
// the only server-side knob left is whether soft-deleted loans are included —
// that one genuinely widens the result rather than narrowing it. The endpoint
// still accepts the older scoping/search params; the UI no longer sends them.
/**
 * The last loans list fetched, so returning from a loan detail can paint the grid
 * immediately instead of waiting on a round trip.
 *
 * Stale-while-revalidate rather than a TTL: `LoansPage` paints this, then always
 * refetches and replaces it. Other people are editing this data, so any expiry
 * we picked would be wrong in both directions — too long and a colleague's edit
 * is missed, too short and we are back to reloading on every visit. Revalidating
 * unconditionally needs no such number.
 *
 * Module-level, not a context: it must outlive the page unmount (which is the
 * entire point), there is exactly one consumer, and a provider above the route
 * would buy the same lifetime with more ceremony.
 */
let loanListCache: { key: string; rows: LoanListItem[] } | null = null;

/** Cache identity — the one server-side knob that changes what comes back. */
export const loanListCacheKey = (includeDeleted: boolean) => includeDeleted ? 'all' : 'active';

/** Rows to paint now, or null if nothing usable is held. */
export function cachedLoanList(key: string): LoanListItem[] | null {
  return loanListCache?.key === key ? loanListCache.rows : null;
}

export function invalidateLoanList(): void {
  loanListCache = null;
}

export const listLoans = async (params?: { includeDeleted?: boolean }) => {
  const qs = new URLSearchParams();
  if (params?.includeDeleted) qs.set('includeDeleted', 'true');
  const q = qs.toString();
  const rows = await http<LoanListItem[]>(`/api/loans${q ? `?${q}` : ''}`);
  loanListCache = { key: loanListCacheKey(!!params?.includeDeleted), rows };
  return rows;
};
export const getLoan = (id: number) => http<Loan>(`/api/loans/${id}`);
export const createLoan = (body: CreateLoanRequest) =>
  http<{ id: number }>('/api/loans', { method: 'POST', body: JSON.stringify(body) });
export const updateLoan = (id: number, body: UpdateLoanRequest) =>
  http<void>(`/api/loans/${id}`, { method: 'PATCH', body: JSON.stringify(body) });
export const deleteLoan = (id: number) =>
  http<void>(`/api/loans/${id}`, { method: 'DELETE' });
export const restoreLoan = (id: number) =>
  http<void>(`/api/loans/${id}/restore`, { method: 'POST' });

// Notes
export const listNotes = (loanId: number) => http<LoanNote[]>(`/api/loans/${loanId}/notes`);
export const createNote = (loanId: number, body: CreateNoteRequest) =>
  http<{ id: number }>(`/api/loans/${loanId}/notes`, { method: 'POST', body: JSON.stringify(body) });
export const deleteNote = (loanId: number, noteId: number) =>
  http<void>(`/api/loans/${loanId}/notes/${noteId}`, { method: 'DELETE' });

// Requests
export const listRequests = (params?: { status?: string; mine?: boolean; loanId?: number }) => {
  const qs = new URLSearchParams();
  if (params?.status) qs.set('status', params.status);
  if (params?.mine) qs.set('mine', 'true');
  if (params?.loanId) qs.set('loanId', String(params.loanId));
  const q = qs.toString();
  return http<LoanRequest[]>(`/api/requests${q ? `?${q}` : ''}`);
};
export const listLoanRequests = (loanId: number) =>
  http<LoanRequest[]>(`/api/loans/${loanId}/requests`);
export const createRequest = (body: CreateRequestRequest) =>
  http<{ id: number }>('/api/requests', { method: 'POST', body: JSON.stringify(body) });
export const updateRequest = (id: number, body: UpdateRequestRequest) =>
  http<void>(`/api/requests/${id}`, { method: 'PATCH', body: JSON.stringify(body) });
export const deleteRequest = (id: number) => http<void>(`/api/requests/${id}`, { method: 'DELETE' });

// Import (admin)
// /api/loan-imports, not /api/loans/imports: the latter is matched by the loans/{id}
// route and resolves to getLoan with id = 'imports'. See the handler for the detail.
// The whole portfolio for the Excel exports. Admin only, unfiltered, and deliberately not
// paged: the exports are the whole book by decision, so there is no page size to get wrong.
export const getPortfolioExport = () => http<PortfolioExport>('/api/reports/export');

export const listLoanImports = (limit?: number) =>
  http<LoanImportSummary[]>(`/api/loan-imports${limit ? `?limit=${limit}` : ''}`);

export const importLoans = (body: LoanImportRequest) =>
  http<LoanImportResult>('/api/loans/import', { method: 'POST', body: JSON.stringify(body) });

/** What the file was, so the import history can name it. All of it optional. */
export interface ImportFileInfo {
  fileName?: string;
  /** SHA-256 of the uploaded bytes, hex — lets a file be matched to its import later. */
  fileHash?: string;
  fileSize?: number;
}

/**
 * Import an extract in batches. Prefer this over calling `importLoans` directly with a
 * whole file — see chunkedImport.ts for why a single 5,000-row request is a timeout risk.
 *
 * One `clientRef` is minted here and repeated on every batch, which is what makes the
 * server treat them as one import rather than one per request. It is minted before the
 * first batch is sent on purpose: the server writes the import record on first contact, so
 * a file that fails on batch 1 still leaves a record of the attempt. File metadata rides
 * along on every batch — the server keeps the first non-null it sees, so it does not matter
 * which batch carries it.
 */
export const importLoansChunked = (
  rows: readonly LoanImportRow[],
  file: ImportFileInfo = {},
  onProgress?: (p: ImportProgress) => void,
) => {
  const clientRef = crypto.randomUUID();
  return runChunkedImport(
    rows,
    (batch, meta) => importLoans({
      rows: batch,
      clientRef,
      seq: meta.seq,
      batchCount: meta.batchCount,
      totalRows: rows.length,
      ...file,
    }),
    { onProgress },
  );
};

/** SHA-256 of an uploaded file, hex. Stored with the import so the file can be identified. */
export async function hashFile(buf: ArrayBuffer): Promise<string | undefined> {
  // Requires a secure context; localhost counts. Not worth failing an import over.
  if (!crypto?.subtle) return undefined;
  try {
    const digest = await crypto.subtle.digest('SHA-256', buf);
    return [...new Uint8Array(digest)].map(b => b.toString(16).padStart(2, '0')).join('');
  } catch {
    return undefined;
  }
}

// Semi-Annual Reports
export const listSar = (loanId: number) =>
  http<SemiAnnualReport[]>(`/api/loans/${loanId}/sar`);
export const createSar = (loanId: number, body: CreateSemiAnnualReportRequest) =>
  http<{ id: number }>(`/api/loans/${loanId}/sar`, { method: 'POST', body: JSON.stringify(body) });
export const deleteSar = (id: number) =>
  http<void>(`/api/sar/${id}`, { method: 'DELETE' });

// 1544 Invoices
export const listInvoices1544 = (loanId: number) =>
  http<Invoice1544[]>(`/api/loans/${loanId}/invoices1544`);
export const createInvoice1544 = (loanId: number, body: CreateInvoice1544Request) =>
  http<{ id: number }>(`/api/loans/${loanId}/invoices1544`, { method: 'POST', body: JSON.stringify(body) });
export const deleteInvoice1544 = (id: number) =>
  http<void>(`/api/invoices1544/${id}`, { method: 'DELETE' });

// Audit
export const getLoanAudit = (loanId: number) => http<AuditEntry[]>(`/api/loans/${loanId}/audit`);

// Reports
